/*
 * Copyright (c) 2017-present, flop! contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 * https://framagit.org/flopedt/flopedt
 */

// Like Radix Vue's useForwardPropsEmits, with the possibility to exclude certain keys and models.

import type { ComputedRef, DefineProps, MaybeRefOrGetter } from 'vue'
import type { ExtractEventEmitter, ExtractEventNamesFromEmitter, Pretty, VueEventEmitter } from '@/utils/types.ts'

import { useEmitAsProps, useForwardProps } from 'reka-ui'
import { camelize, computed, toHandlerKey, toValue } from 'vue'

type ExcludeUpdateEvent<T extends string> = T extends `update:${string}` ? never : T

type SkippedToProps<T extends string> = { [K in T]: K extends `@${infer E}` ? `on${Capitalize<E>}` : K }[T]

type EmitterToProps<E extends VueEventEmitter> = {
  [K in ExtractEventNamesFromEmitter<E> as `on${Capitalize<K>}`]: ExtractEventEmitter<E, K>
}

type KeysOf<P, E> =
  | (P extends Record<string, unknown> ? keyof P : never)
  | (E extends VueEventEmitter ? `@${ExcludeUpdateEvent<ExtractEventNamesFromEmitter<E>>}` : never)

export function useForwardPropsEmitsSkip<
  P extends Record<string, unknown>,
  E extends VueEventEmitter,
  S extends Array<KeysOf<NoInfer<P>, NoInfer<E>>> = never,
>(
  props: MaybeRefOrGetter<DefineProps<P, never>> | null,
  emits: E | null,
  skip?: MaybeRefOrGetter<S>,
  models?: MaybeRefOrGetter<string[]>
): ComputedRef<Pretty<Omit<P & EmitterToProps<E>, SkippedToProps<S[number]>>>> {
  const forwardProps = props ? useForwardProps(props) : null
  const forwardEmits = emits ? useEmitAsProps(emits) : null

  return computed(() => {
    const skipValue = toValue(skip)
    const modelsValue = toValue(models)
    const res: Record<string, unknown> = {}

    if (forwardProps) Object.assign(res, forwardProps.value)
    if (forwardEmits) Object.assign(res, forwardEmits)

    if (skipValue) {
      for (const s of skipValue) {
        const key = s.startsWith('@') ? toHandlerKey(camelize(s.slice(1))) : s
        delete res[key]
      }
    }

    delete res.modelValue
    delete res[toHandlerKey(camelize('update:modelValue'))]

    if (modelsValue) {
      for (const m of modelsValue) {
        delete res[m]
        delete res[toHandlerKey(camelize(`update:${m}`))]
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Quite a pain to deal
    return res as any
  })
}
